import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  Clock,
  ArrowRight,
  X,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ==================== DATE HELPERS ====================

const parseDate = (str) => {
  if (!str) return null;
  const d = new Date(str + 'T00:00:00');
  return isNaN(d.getTime()) ? null : d;
};

const startOfDay = (d) => {
  const r = new Date(d);
  r.setHours(0, 0, 0, 0);
  return r;
};

const addDays = (d, n) => {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
};

const addMonths = (d, n) => {
  const r = new Date(d);
  r.setMonth(r.getMonth() + n);
  return r;
};

// Monday-start week
const startOfWeek = (d) => {
  const r = startOfDay(d);
  const day = r.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  r.setDate(r.getDate() + diff);
  return r;
};

const startOfMonth = (d) => {
  const r = startOfDay(d);
  r.setDate(1);
  return r;
};

const sameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const daysBetween = (a, b) =>
  Math.round((startOfDay(b) - startOfDay(a)) / (1000 * 60 * 60 * 24));

const formatDateShort = (d) =>
  d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

const toDateStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// ==================== COLORS ====================

const STATUS_DOT = {
  'Complete':    'bg-emerald-500',
  'In Progress': 'bg-blue-500',
  'Not Started': 'bg-zinc-400',
  'Delayed':     'bg-red-500',
};

const STATUS_TEXT = {
  'Complete':    'text-emerald-600',
  'In Progress': 'text-blue-600',
  'Not Started': 'text-zinc-500',
  'Delayed':     'text-red-600',
};

// Saturated palette for project color-coding (one per project)
const PROJECT_PALETTE = [
  '#FFC40C', // gold (brand)
  '#3B82F6', // blue
  '#10B981', // emerald
  '#EF4444', // red
  '#8B5CF6', // violet
  '#EC4899', // pink
  '#14B8A6', // teal
  '#F97316', // orange
  '#06B6D4', // cyan
  '#84CC16', // lime
];

const projectColor = (idx) => PROJECT_PALETTE[idx % PROJECT_PALETTE.length];

// ==================== EVENT LAYOUT (per week row) ====================

// Given a week's Monday and all events, produce lanes (non-overlapping rows of placed chips)
const layoutWeekEvents = (weekStart, events) => {
  const weekEndExcl = addDays(weekStart, 7);
  const placed = events
    .filter(e => e.end >= weekStart && e.start < weekEndExcl)
    .map(e => {
      const clipStart = e.start < weekStart ? weekStart : e.start;
      const clipEndExcl = addDays(e.end, 1) > weekEndExcl ? weekEndExcl : addDays(e.end, 1);
      const startCol = daysBetween(weekStart, clipStart);
      const span = Math.max(1, daysBetween(clipStart, clipEndExcl));
      return { event: e, startCol, span };
    })
    .sort((a, b) => a.startCol - b.startCol || b.span - a.span);

  const lanes = [];
  for (const item of placed) {
    let laneIdx = 0;
    for (;;) {
      if (!lanes[laneIdx]) lanes[laneIdx] = [];
      const conflict = lanes[laneIdx].some(
        x => !(item.startCol + item.span <= x.startCol || x.startCol + x.span <= item.startCol)
      );
      if (!conflict) {
        lanes[laneIdx].push(item);
        break;
      }
      laneIdx++;
    }
  }
  return lanes;
};

// ==================== PHASE DETAIL PANEL ====================

const PhasePanel = ({ phase, project, onClose, onSave }) => {
  const [startDate, setStartDate] = useState(phase.start_date || '');
  const [endDate, setEndDate] = useState(phase.end_date || '');
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const handleSave = async () => {
    if (!startDate || !endDate) {
      toast.error('Both dates are required');
      return;
    }
    if (startDate > endDate) {
      toast.error('Start date must be before end date');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/timeline/phase/${phase.phase_id}/dates`, {
        start_date: startDate,
        end_date: endDate
      }, { withCredentials: true });
      toast.success('Dates updated');
      onSave(phase.phase_id, startDate, endDate);
    } catch {
      toast.error('Failed to update dates');
    }
    setSaving(false);
  };

  const budgetPct = phase.budget > 0 ? Math.min((phase.spent / phase.budget) * 100, 100) : 0;

  return (
    <div
      className="fixed inset-y-0 right-0 w-80 bg-white border-l-2 border-black shadow-[-8px_0_0_0_rgba(0,0,0,0.08)] z-50 flex flex-col"
      data-testid="phase-detail-panel"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b-2 border-black bg-zinc-50">
        <span className="font-heading font-bold text-xs uppercase tracking-[0.15em] text-zinc-900">Phase Details</span>
        <button onClick={onClose} className="p-1 hover:bg-zinc-200 text-zinc-600 transition" data-testid="phase-panel-close">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-5">
        <div>
          <h3 className="font-heading font-bold text-base uppercase tracking-wide text-zinc-900 mb-1">{phase.name}</h3>
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 ${STATUS_DOT[phase.status] || STATUS_DOT['Not Started']}`} />
            <span className={`text-xs font-heading font-semibold uppercase tracking-wider ${STATUS_TEXT[phase.status] || STATUS_TEXT['Not Started']}`}>
              {phase.status}
            </span>
          </div>
        </div>

        <div className="bg-zinc-50 border border-zinc-200 p-3">
          <p className="text-[10px] font-heading font-semibold uppercase tracking-[0.15em] text-zinc-400 mb-1">Project</p>
          <button
            onClick={() => navigate(`/projects/${project.project_id}`)}
            className="text-sm font-medium text-zinc-900 hover:text-primary transition-colors flex items-center gap-1"
            data-testid="phase-panel-project-link"
          >
            {project.name} <ArrowRight className="w-3 h-3" />
          </button>
        </div>

        <div>
          <p className="text-[10px] font-heading font-semibold uppercase tracking-[0.15em] text-zinc-400 mb-2">Budget</p>
          <div className="flex justify-between text-sm mb-1">
            <span className="font-mono font-medium text-zinc-900">
              ${phase.spent.toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </span>
            <span className="font-mono text-zinc-400">
              of ${phase.budget.toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </span>
          </div>
          <div className="h-1.5 bg-zinc-100 overflow-hidden">
            <div
              className={`h-full ${budgetPct > 90 ? 'bg-red-500' : budgetPct > 70 ? 'bg-amber-400' : 'bg-emerald-500'}`}
              style={{ width: `${budgetPct}%` }}
            />
          </div>
        </div>

        <div>
          <p className="text-[10px] font-heading font-semibold uppercase tracking-[0.15em] text-zinc-400 mb-2">Schedule</p>
          {phase.dates_auto && (
            <p className="text-[10px] text-amber-500 font-mono mb-2">DATES AUTO-GENERATED</p>
          )}
          <div className="space-y-3">
            <div>
              <label className="block text-[10px] font-heading font-semibold uppercase tracking-wider text-zinc-500 mb-1">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                data-testid="phase-start-date"
                className="w-full h-9 border border-zinc-200 bg-white text-zinc-900 text-sm px-3 font-mono focus:outline-none focus:border-primary transition-colors"
              />
            </div>
            <div>
              <label className="block text-[10px] font-heading font-semibold uppercase tracking-wider text-zinc-500 mb-1">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                data-testid="phase-end-date"
                className="w-full h-9 border border-zinc-200 bg-white text-zinc-900 text-sm px-3 font-mono focus:outline-none focus:border-primary transition-colors"
              />
            </div>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="phase-save-dates"
            className="w-full mt-3 h-9 bg-primary text-black font-heading font-bold uppercase tracking-wider text-xs hover:bg-primary-hover transition-colors disabled:opacity-50 border-2 border-black shadow-[3px_3px_0_0_rgba(0,0,0,1)] hover:shadow-[4px_4px_0_0_rgba(0,0,0,1)] hover:-translate-y-0.5"
          >
            {saving ? 'Saving...' : 'Save Dates'}
          </button>
        </div>
      </div>

      <div className="p-4 border-t-2 border-black">
        <button
          onClick={() => navigate(`/projects/${project.project_id}`)}
          data-testid="phase-panel-open-project"
          className="w-full h-9 border-2 border-black bg-white text-zinc-900 font-heading font-semibold uppercase tracking-wider text-xs hover:bg-zinc-50 transition-colors flex items-center justify-center gap-2"
        >
          Open Project <ArrowRight className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
};

// ==================== EVENT CHIP (drag-enabled) ====================

const EventChip = ({ event, startCol, span, laneIdx, laneHeight, cols, onClick, onCommit, size = 'md', allowDrag = false }) => {
  const chipRef = useRef(null);
  const [drag, setDrag] = useState(null);

  const sizeCls = {
    sm: { h: 10, top: 2, px: 'px-1', text: 'text-[8px]', gap: 'gap-0.5' },
    md: { h: 20, top: 2, px: 'px-1.5', text: 'text-[10px]', gap: 'gap-1' },
    lg: { h: 26, top: 4, px: 'px-2', text: 'text-[11px]', gap: 'gap-1.5' },
  }[size];

  const startDrag = (mode) => (e) => {
    if (!allowDrag) return; // non-draggable: let onClick handle it
    e.stopPropagation();
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.preventDefault();
    const parent = chipRef.current?.parentElement;
    if (!parent) { onClick(event); return; }
    const dayWidth = parent.getBoundingClientRect().width / cols;
    if (!dayWidth) { onClick(event); return; }
    const originX = e.clientX;
    const origStart = event.start;
    const origEnd = event.end;
    setDrag({ mode, dayWidth, dxDays: 0, moved: false, snapGuide: null });

    const SNAP_THRESHOLD = 3;
    const addD = (d, n) => { const r = new Date(d); r.setHours(0, 0, 0, 0); r.setDate(r.getDate() + n); return r; };
    const absDays = (a, b) => Math.abs(daysBetween(a, b));

    const nearestSnap = (d) => {
      const day = d.getDay();
      const prevDiff = day === 0 ? -6 : 1 - day; // negative/zero
      const prevMonday = addD(d, prevDiff);
      const nextMonday = addD(prevMonday, 7);
      const prevDelta = absDays(d, prevMonday);
      const nextDelta = absDays(d, nextMonday);
      const monday = prevDelta <= nextDelta ? prevMonday : nextMonday;
      const mondayDelta = Math.min(prevDelta, nextDelta);

      const firstThis = new Date(d); firstThis.setHours(0, 0, 0, 0); firstThis.setDate(1);
      const firstNext = new Date(d); firstNext.setHours(0, 0, 0, 0); firstNext.setMonth(firstNext.getMonth() + 1); firstNext.setDate(1);
      const thisDelta = absDays(d, firstThis);
      const nextD = absDays(d, firstNext);
      const month = thisDelta <= nextD ? firstThis : firstNext;
      const monthDelta = Math.min(thisDelta, nextD);

      if (monthDelta <= SNAP_THRESHOLD && monthDelta <= mondayDelta) {
        return { date: month, kind: 'month' };
      }
      if (mondayDelta <= SNAP_THRESHOLD) {
        return { date: monday, kind: 'week' };
      }
      return null;
    };

    const computeSnapped = (rawDxDays) => {
      let rawStart = origStart, rawEnd = origEnd;
      if (mode === 'move') {
        rawStart = addD(origStart, rawDxDays);
        rawEnd = addD(origEnd, rawDxDays);
      } else if (mode === 'resize-left') {
        rawStart = addD(origStart, rawDxDays);
        if (rawStart > origEnd) rawStart = origEnd;
      } else if (mode === 'resize-right') {
        rawEnd = addD(origEnd, rawDxDays);
        if (rawEnd < origStart) rawEnd = origStart;
      }

      let snappedStart = rawStart, snappedEnd = rawEnd, snapGuide = null;
      if (mode === 'resize-left') {
        const s = nearestSnap(rawStart);
        if (s) {
          snappedStart = s.date > origEnd ? origEnd : s.date;
          snapGuide = { ...s, date: snappedStart, edge: 'start' };
        }
      } else if (mode === 'resize-right') {
        const s = nearestSnap(rawEnd);
        if (s) {
          snappedEnd = s.date < origStart ? origStart : s.date;
          snapGuide = { ...s, date: snappedEnd, edge: 'end' };
        }
      } else { // move
        const sS = nearestSnap(rawStart);
        const sE = nearestSnap(rawEnd);
        let useStart = null;
        if (sS && sE) {
          const dS = absDays(rawStart, sS.date);
          const dE = absDays(rawEnd, sE.date);
          useStart = dS <= dE;
        } else if (sS) useStart = true;
        else if (sE) useStart = false;
        if (useStart === true) {
          const delta = daysBetween(rawStart, sS.date);
          snappedStart = addD(rawStart, delta);
          snappedEnd = addD(rawEnd, delta);
          snapGuide = { ...sS, date: snappedStart, edge: 'start' };
        } else if (useStart === false) {
          const delta = daysBetween(rawEnd, sE.date);
          snappedStart = addD(rawStart, delta);
          snappedEnd = addD(rawEnd, delta);
          snapGuide = { ...sE, date: snappedEnd, edge: 'end' };
        }
      }
      return { snappedStart, snappedEnd, snapGuide };
    };

    const handleMove = (ev) => {
      const dx = ev.clientX - originX;
      const rawDxDays = Math.round(dx / dayWidth);
      const { snappedStart, snappedEnd, snapGuide } = computeSnapped(rawDxDays);
      const effDxStart = daysBetween(origStart, snappedStart);
      const effDxEnd = daysBetween(origEnd, snappedEnd);
      setDrag(d => d ? {
        ...d,
        dxDays: rawDxDays,
        effDxStart,
        effDxEnd,
        snappedStart,
        snappedEnd,
        snapGuide,
        moved: d.moved || Math.abs(dx) > 3,
      } : null);
    };
    const finish = (ev) => {
      document.removeEventListener('pointermove', handleMove);
      document.removeEventListener('pointerup', finish);
      document.removeEventListener('pointercancel', finish);
      const finalDx = ev.clientX - originX;
      const rawDxDays = Math.round(finalDx / dayWidth);
      const didMove = Math.abs(finalDx) > 3;
      const { snappedStart, snappedEnd } = computeSnapped(rawDxDays);
      setDrag(null);
      if (!didMove) {
        if (mode === 'move') onClick(event);
        return;
      }
      const sStr = toDateStr(snappedStart);
      const eStr = toDateStr(snappedEnd);
      if (sStr === event.phase.start_date && eStr === event.phase.end_date) {
        if (mode === 'move') onClick(event);
        return;
      }
      onCommit(event, sStr, eStr);
    };
    document.addEventListener('pointermove', handleMove);
    document.addEventListener('pointerup', finish);
    document.addEventListener('pointercancel', finish);
  };

  // Live preview offsets (use snapped deltas when available)
  let extraLeftPx = 0, extraWidthPx = 0, transform = '';
  if (drag && drag.moved) {
    const dayW = drag.dayWidth;
    if (drag.mode === 'move') {
      const dx = (drag.effDxStart ?? drag.dxDays) * dayW;
      transform = `translateX(${dx}px)`;
    } else if (drag.mode === 'resize-left') {
      const dx = (drag.effDxStart ?? drag.dxDays) * dayW;
      extraLeftPx = dx;
      extraWidthPx = -dx;
    } else if (drag.mode === 'resize-right') {
      const dx = (drag.effDxEnd ?? drag.dxDays) * dayW;
      extraWidthPx = dx;
    }
  }

  const previewDates = (() => {
    if (!drag || !drag.moved) return null;
    if (drag.snappedStart && drag.snappedEnd) {
      return { s: drag.snappedStart, e: drag.snappedEnd };
    }
    return null;
  })();

  const cursorCls = !allowDrag
    ? 'cursor-pointer'
    : drag?.mode === 'move'
      ? 'cursor-grabbing'
      : 'cursor-grab';

  return (
    <div
      ref={chipRef}
      role="button"
      tabIndex={0}
      onPointerDown={startDrag('move')}
      onClick={allowDrag ? undefined : (e) => { e.stopPropagation(); onClick(event); }}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(event); } }}
      className={`absolute pointer-events-auto border-2 border-black shadow-[2px_2px_0_0_rgba(0,0,0,1)] ${drag ? 'z-40' : 'hover:shadow-[3px_3px_0_0_rgba(0,0,0,1)] hover:-translate-y-0.5'} transition-shadow select-none ${cursorCls}`}
      style={{
        left: `calc(${(startCol / cols) * 100}% + 2px + ${extraLeftPx}px)`,
        width: `calc(${(span / cols) * 100}% - 6px + ${extraWidthPx}px)`,
        top: sizeCls.top + laneIdx * laneHeight,
        height: sizeCls.h,
        backgroundColor: event.projectColor,
        transform,
      }}
      data-testid={`gantt-event-${event.id}`}
      title={`${event.project.name} • ${event.phase.name} • ${event.phase.status}`}
    >
      {size !== 'sm' && (
        <div className={`flex items-center h-full ${sizeCls.px} ${sizeCls.gap} overflow-hidden`}>
          <div className={`w-1.5 h-1.5 flex-shrink-0 ${STATUS_DOT[event.phase.status] || STATUS_DOT['Not Started']}`} />
          <span className={`${sizeCls.text} font-heading font-bold uppercase tracking-wide text-black truncate`}>
            {event.phase.name}
          </span>
        </div>
      )}

      {/* Resize handles (only on draggable + non-tiny chips) */}
      {allowDrag && size !== 'sm' && (
        <>
          <div
            onPointerDown={startDrag('resize-left')}
            className="absolute left-0 top-0 bottom-0 w-1.5 cursor-ew-resize hover:bg-black/25"
            data-testid={`gantt-event-${event.id}-handle-l`}
          />
          <div
            onPointerDown={startDrag('resize-right')}
            className="absolute right-0 top-0 bottom-0 w-1.5 cursor-ew-resize hover:bg-black/25"
            data-testid={`gantt-event-${event.id}-handle-r`}
          />
        </>
      )}

      {/* Live date preview tooltip during drag */}
      {previewDates && (
        <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1 px-2 py-1 bg-black text-white text-[10px] font-mono whitespace-nowrap border-2 border-primary z-50 pointer-events-none">
          {formatDateShort(previewDates.s)} → {formatDateShort(previewDates.e)}
        </div>
      )}

      {/* Snap-to-week/month guide line + badge */}
      {drag?.snapGuide && (
        <div
          className="absolute pointer-events-none z-40"
          style={{
            [drag.snapGuide.edge === 'start' ? 'left' : 'right']: -1,
            top: -2000,
            bottom: -2000,
            width: 2,
          }}
          data-testid="gantt-snap-guide"
        >
          <div className={`w-full h-full border-l-2 border-dashed ${drag.snapGuide.kind === 'month' ? 'border-orange-500' : 'border-[#FFC40C]'}`} />
          <div
            className={`absolute ${drag.snapGuide.edge === 'start' ? 'left-1' : 'right-1'} px-1.5 py-0.5 text-[9px] font-heading font-bold uppercase tracking-wider text-black border-2 border-black ${drag.snapGuide.kind === 'month' ? 'bg-orange-400' : 'bg-primary'}`}
            style={{ top: 2010 }}
            data-testid={`gantt-snap-badge-${drag.snapGuide.kind}`}
          >
            {drag.snapGuide.kind}
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== MONTH VIEW ====================

const MonthView = ({ anchor, events, onEventClick, onEventCommit }) => {
  const monthStart = startOfMonth(anchor);
  const gridStart = startOfWeek(monthStart);
  const monthEndExcl = addMonths(monthStart, 1);
  const gridEndExcl = (() => {
    const lastDay = addDays(monthEndExcl, -1);
    return addDays(startOfWeek(lastDay), 7);
  })();

  const weeks = [];
  for (let cur = gridStart; cur < gridEndExcl; cur = addDays(cur, 7)) weeks.push(cur);

  const today = startOfDay(new Date());
  const MAX_LANES = 3;
  const LANE_HEIGHT = 22;

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-white">
      {/* Day header */}
      <div className="grid grid-cols-7 border-b-2 border-black bg-zinc-50 flex-shrink-0">
        {['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'].map((d, i) => (
          <div
            key={d}
            className={`px-3 py-2 text-[10px] font-heading font-bold uppercase tracking-[0.15em] text-zinc-600 ${i < 6 ? 'border-r border-zinc-200' : ''}`}
          >
            {d}
          </div>
        ))}
      </div>

      {/* Weeks */}
      <div className="flex-1 grid overflow-auto" style={{ gridTemplateRows: `repeat(${weeks.length}, minmax(120px, 1fr))` }}>
        {weeks.map((weekStart, wIdx) => {
          const lanes = layoutWeekEvents(weekStart, events);
          const overflow = Math.max(0, lanes.length - MAX_LANES);
          return (
            <div key={weekStart.toISOString()} className={`grid grid-cols-7 relative ${wIdx < weeks.length - 1 ? 'border-b border-zinc-200' : ''}`}>
              {/* Background cells */}
              {[0, 1, 2, 3, 4, 5, 6].map(col => {
                const d = addDays(weekStart, col);
                const isOtherMonth = d.getMonth() !== monthStart.getMonth();
                const isToday = sameDay(d, today);
                const isWeekend = col >= 5;
                return (
                  <div
                    key={col}
                    className={`${col < 6 ? 'border-r border-zinc-200' : ''} px-2 pt-1.5 pb-1 ${isOtherMonth ? 'bg-zinc-50' : isWeekend ? 'bg-zinc-50/40' : 'bg-white'}`}
                    data-testid={`month-cell-${d.toISOString().split('T')[0]}`}
                  >
                    <div className="flex items-center justify-between">
                      <span
                        className={`text-xs font-mono font-semibold ${
                          isToday
                            ? 'bg-primary text-black px-1.5 py-0.5 border border-black font-bold'
                            : isOtherMonth ? 'text-zinc-300' : 'text-zinc-700'
                        }`}
                      >
                        {d.getDate()}
                      </span>
                    </div>
                  </div>
                );
              })}

              {/* Event chips overlay */}
              <div className="absolute inset-x-0 pointer-events-none" style={{ top: 28, bottom: 4 }}>
                {lanes.slice(0, MAX_LANES).map((lane, laneIdx) =>
                  lane.map(({ event, startCol, span }) => (
                    <EventChip
                      key={`${event.id}-${laneIdx}`}
                      event={event}
                      startCol={startCol}
                      span={span}
                      laneIdx={laneIdx}
                      laneHeight={LANE_HEIGHT}
                      cols={7}
                      onClick={onEventClick}
                      onCommit={onEventCommit}
                      allowDrag
                      size="md"
                    />
                  ))
                )}
                {overflow > 0 && (
                  <div
                    className="absolute right-2 text-[10px] font-heading font-bold uppercase tracking-wider text-zinc-500"
                    style={{ bottom: 2 }}
                  >
                    +{overflow} more
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ==================== WEEK VIEW ====================

const WeekView = ({ anchor, events, onEventClick, onEventCommit }) => {
  const weekStart = startOfWeek(anchor);
  const today = startOfDay(new Date());
  const lanes = layoutWeekEvents(weekStart, events);
  const LANE_HEIGHT = 36;

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-white">
      {/* Day header */}
      <div className="grid grid-cols-7 border-b-2 border-black bg-zinc-50 flex-shrink-0">
        {[0, 1, 2, 3, 4, 5, 6].map(i => {
          const d = addDays(weekStart, i);
          const isToday = sameDay(d, today);
          return (
            <div
              key={i}
              className={`px-3 py-3 text-center ${i < 6 ? 'border-r border-zinc-200' : ''}`}
              data-testid={`week-header-${d.toISOString().split('T')[0]}`}
            >
              <div className="text-[10px] font-heading font-bold uppercase tracking-[0.15em] text-zinc-400">
                {d.toLocaleDateString('en-US', { weekday: 'short' })}
              </div>
              <div className={`mt-1 ${isToday ? 'inline-block bg-primary text-black border-2 border-black px-2 py-0.5' : ''}`}>
                <span className="text-xl sm:text-2xl font-heading font-bold text-zinc-900 leading-none">
                  {d.getDate()}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Events body */}
      <div className="flex-1 overflow-auto relative">
        <div
          className="relative grid grid-cols-7"
          style={{ minHeight: Math.max(8, lanes.length + 1) * LANE_HEIGHT + 24 }}
        >
          {[0, 1, 2, 3, 4, 5, 6].map(col => {
            const d = addDays(weekStart, col);
            const isToday = sameDay(d, today);
            const isWeekend = col >= 5;
            return (
              <div
                key={col}
                className={`${col < 6 ? 'border-r border-zinc-200' : ''} relative ${isWeekend ? 'bg-zinc-50/40' : ''}`}
              >
                {isToday && <div className="absolute inset-y-0 left-0 w-1 bg-primary z-10" />}
              </div>
            );
          })}

          {/* Event chips */}
          <div className="absolute inset-0 pointer-events-none" style={{ top: 12 }}>
            {lanes.map((lane, laneIdx) =>
              lane.map(({ event, startCol, span }) => (
                <EventChip
                  key={`${event.id}-${laneIdx}`}
                  event={event}
                  startCol={startCol}
                  span={span}
                  laneIdx={laneIdx}
                  laneHeight={LANE_HEIGHT}
                  cols={7}
                  onClick={onEventClick}
                  onCommit={onEventCommit}
                  allowDrag
                  size="lg"
                />
              ))
            )}
            {lanes.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center text-zinc-400">
                  <Calendar className="w-10 h-10 mx-auto mb-2 opacity-40" />
                  <p className="text-xs font-heading font-semibold uppercase tracking-wider">No phases this week</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== QUARTER VIEW (3 mini-months) ====================

const MiniMonth = ({ month, events, onEventClick }) => {
  const gridStart = startOfWeek(month);
  const monthEndExcl = addMonths(month, 1);
  const gridEndExcl = (() => {
    const lastDay = addDays(monthEndExcl, -1);
    return addDays(startOfWeek(lastDay), 7);
  })();

  const weeks = [];
  for (let cur = gridStart; cur < gridEndExcl; cur = addDays(cur, 7)) weeks.push(cur);
  const today = startOfDay(new Date());
  const LANE_HEIGHT = 11;
  const MAX_LANES = 3;

  return (
    <div className="bg-white border-2 border-black shadow-[4px_4px_0_0_rgba(0,0,0,1)]">
      <div className="px-3 py-2 border-b-2 border-black bg-zinc-50">
        <h3 className="font-heading font-bold text-sm uppercase tracking-wider text-zinc-900">
          {month.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </h3>
      </div>
      <div className="grid grid-cols-7 border-b border-zinc-200 bg-zinc-50">
        {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
          <div key={`mini-dow-${i}`} className="text-center py-1 text-[9px] font-heading font-bold uppercase tracking-wider text-zinc-400">
            {d}
          </div>
        ))}
      </div>
      {weeks.map((weekStart, wIdx) => {
        const lanes = layoutWeekEvents(weekStart, events);
        return (
          <div
            key={weekStart.toISOString()}
            className={`grid grid-cols-7 relative ${wIdx < weeks.length - 1 ? 'border-b border-zinc-100' : ''}`}
            style={{ minHeight: 60 }}
          >
            {[0, 1, 2, 3, 4, 5, 6].map(col => {
              const d = addDays(weekStart, col);
              const isOther = d.getMonth() !== month.getMonth();
              const isToday = sameDay(d, today);
              return (
                <div
                  key={col}
                  className={`${col < 6 ? 'border-r border-zinc-100' : ''} px-1 pt-1 ${isOther ? 'bg-zinc-50' : ''}`}
                >
                  <div className={`text-[10px] font-mono ${isToday ? 'bg-primary text-black px-1 font-bold' : isOther ? 'text-zinc-300' : 'text-zinc-600'}`}>
                    {d.getDate()}
                  </div>
                </div>
              );
            })}
            <div className="absolute inset-x-0 pointer-events-none" style={{ top: 18, bottom: 2 }}>
              {lanes.slice(0, MAX_LANES).map((lane, laneIdx) =>
                lane.map(({ event, startCol, span }) => (
                  <EventChip
                    key={`${event.id}-${laneIdx}`}
                    event={event}
                    startCol={startCol}
                    span={span}
                    laneIdx={laneIdx}
                    laneHeight={LANE_HEIGHT}
                    cols={7}
                    onClick={onEventClick}
                    size="sm"
                  />
                ))
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const QuarterView = ({ anchor, events, onEventClick }) => {
  const months = [0, 1, 2].map(i => startOfMonth(addMonths(anchor, i)));
  return (
    <div className="flex-1 overflow-auto bg-zinc-100 p-4">
      <div className="grid gap-4 lg:grid-cols-3">
        {months.map(m => (
          <MiniMonth key={m.toISOString()} month={m} events={events} onEventClick={onEventClick} />
        ))}
      </div>
    </div>
  );
};

// ==================== MAIN PAGE ====================

const GanttPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(() =>
    (typeof window !== 'undefined' && window.innerWidth < 768) ? 'week' : 'month'
  );
  const [anchor, setAnchor] = useState(() => startOfDay(new Date()));
  const [selectedPhase, setSelectedPhase] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);

  useEffect(() => {
    fetchTimeline();
  }, []);

  const fetchTimeline = async () => {
    try {
      const res = await axios.get(`${API}/timeline/`, { withCredentials: true });
      setData(res.data);
    } catch {
      toast.error('Failed to load timeline');
    } finally {
      setLoading(false);
    }
  };

  // Build flat event list (phases with dates)
  const { events, projectsWithColor, noDateProjects } = useMemo(() => {
    const evts = [];
    const projs = [];
    const noDate = [];
    data.forEach((proj, pIdx) => {
      const color = projectColor(pIdx);
      projs.push({ ...proj, _color: color });
      const phasesWithDates = (proj.phases || []).filter(ph => ph.start_date && ph.end_date);
      if (phasesWithDates.length === 0 && !proj.start_date) {
        noDate.push(proj);
      }
      phasesWithDates.forEach(ph => {
        const s = parseDate(ph.start_date);
        const e = parseDate(ph.end_date);
        if (!s || !e) return;
        evts.push({
          id: ph.phase_id,
          phase: ph,
          project: proj,
          projectColor: color,
          start: s,
          end: e,
        });
      });
    });
    return { events: evts, projectsWithColor: projs, noDateProjects: noDate };
  }, [data]);

  const goPrev = () => {
    if (view === 'week') setAnchor(a => addDays(a, -7));
    else if (view === 'month') setAnchor(a => addMonths(a, -1));
    else setAnchor(a => addMonths(a, -3));
  };
  const goNext = () => {
    if (view === 'week') setAnchor(a => addDays(a, 7));
    else if (view === 'month') setAnchor(a => addMonths(a, 1));
    else setAnchor(a => addMonths(a, 3));
  };
  const goToday = () => setAnchor(startOfDay(new Date()));

  const title = useMemo(() => {
    if (view === 'week') {
      const ws = startOfWeek(anchor);
      const we = addDays(ws, 6);
      if (ws.getMonth() === we.getMonth() && ws.getFullYear() === we.getFullYear()) {
        return `${ws.toLocaleDateString('en-US', { month: 'long' })} ${ws.getDate()}–${we.getDate()}, ${ws.getFullYear()}`;
      }
      return `${formatDateShort(ws)} – ${formatDateShort(we)}, ${we.getFullYear()}`;
    }
    if (view === 'month') {
      return anchor.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
    const qStart = startOfMonth(anchor);
    const qEnd = addMonths(qStart, 2);
    return `${qStart.toLocaleDateString('en-US', { month: 'short' })} – ${qEnd.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`;
  }, [view, anchor]);

  const handleEventClick = (evt) => {
    setSelectedPhase(evt.phase);
    setSelectedProject(evt.project);
  };

  const handleEventCommit = async (evt, newStartStr, newEndStr) => {
    const prevStart = evt.phase.start_date;
    const prevEnd = evt.phase.end_date;
    if (newStartStr === prevStart && newEndStr === prevEnd) return;
    // Optimistic update
    handlePhaseDateSave(evt.phase.phase_id, newStartStr, newEndStr);
    try {
      await axios.put(
        `${API}/timeline/phase/${evt.phase.phase_id}/dates`,
        { start_date: newStartStr, end_date: newEndStr },
        { withCredentials: true }
      );
      toast.success(`${evt.phase.name} rescheduled`);
    } catch {
      toast.error('Failed to reschedule — reverting');
      handlePhaseDateSave(evt.phase.phase_id, prevStart, prevEnd);
    }
  };

  const handlePhaseDateSave = (phaseId, startDate, endDate) => {
    setData(prev => prev.map(p => ({
      ...p,
      phases: p.phases.map(ph =>
        ph.phase_id === phaseId
          ? { ...ph, start_date: startDate, end_date: endDate, dates_auto: false }
          : ph
      )
    })));
    setSelectedPhase(prev => prev ? { ...prev, start_date: startDate, end_date: endDate, dates_auto: false } : null);
  };

  const VIEW_OPTIONS = [
    { key: 'week', label: 'Week' },
    { key: 'month', label: 'Month' },
    { key: 'quarter', label: 'Quarter' },
  ];

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] md:h-screen overflow-hidden bg-zinc-50" data-testid="gantt-page">
      {/* ===== TOOLBAR ===== */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 sm:px-6 py-3 bg-white border-b-2 border-black flex-shrink-0">
        {/* Left: Title */}
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-8 h-8 bg-primary border-2 border-black flex items-center justify-center shadow-[2px_2px_0_0_rgba(0,0,0,1)] flex-shrink-0">
            <Calendar className="w-4 h-4 text-black" />
          </div>
          <div className="min-w-0">
            <h1 className="font-heading text-lg sm:text-xl font-bold uppercase tracking-wide text-zinc-900 leading-none">Timeline</h1>
            <p className="text-[10px] font-mono text-zinc-500 mt-1 truncate" data-testid="gantt-title">{title}</p>
          </div>
        </div>

        {/* Center: Navigation */}
        <div className="flex items-center gap-2">
          <button
            onClick={goToday}
            data-testid="gantt-today-btn"
            className="h-9 px-3 bg-white border-2 border-black text-zinc-900 font-heading font-bold uppercase tracking-wider text-[11px] hover:bg-primary hover:-translate-y-0.5 hover:shadow-[3px_3px_0_0_rgba(0,0,0,1)] transition-all"
          >
            Today
          </button>
          <div className="flex border-2 border-black">
            <button
              onClick={goPrev}
              data-testid="gantt-prev-btn"
              className="w-9 h-9 bg-white flex items-center justify-center text-zinc-900 hover:bg-zinc-100 transition-colors border-r-2 border-black"
              title="Previous"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={goNext}
              data-testid="gantt-next-btn"
              className="w-9 h-9 bg-white flex items-center justify-center text-zinc-900 hover:bg-zinc-100 transition-colors"
              title="Next"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Right: View toggle */}
        <div className="flex border-2 border-black" data-testid="gantt-view-toggle">
          {VIEW_OPTIONS.map((v, i) => (
            <button
              key={v.key}
              onClick={() => setView(v.key)}
              data-testid={`gantt-view-${v.key}`}
              className={`px-4 h-9 text-[11px] font-heading font-bold uppercase tracking-wider transition-colors ${
                view === v.key
                  ? 'bg-primary text-black'
                  : 'bg-white text-zinc-700 hover:bg-zinc-100'
              } ${i > 0 ? 'border-l-2 border-black' : ''}`}
            >
              {v.label}
            </button>
          ))}
        </div>
      </div>

      {/* ===== PROJECT LEGEND ===== */}
      {!loading && projectsWithColor.length > 0 && (
        <div className="flex-shrink-0 px-4 sm:px-6 py-2 bg-white border-b border-zinc-200 overflow-x-auto">
          <div className="flex items-center gap-4 min-w-max">
            <span className="text-[10px] font-heading font-bold uppercase tracking-[0.15em] text-zinc-400 flex-shrink-0">Projects</span>
            {projectsWithColor.map(p => (
              <button
                key={p.project_id}
                onClick={() => navigate(`/projects/${p.project_id}`)}
                className="flex items-center gap-1.5 group flex-shrink-0"
                data-testid={`gantt-legend-${p.project_id}`}
              >
                <span
                  className="w-3 h-3 border-2 border-black flex-shrink-0"
                  style={{ backgroundColor: p._color }}
                />
                <span className="text-xs font-medium text-zinc-700 group-hover:text-zinc-900 group-hover:underline truncate max-w-[160px]">
                  {p.name}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ===== BODY ===== */}
      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-pulse space-y-3 w-full max-w-2xl px-8">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="h-10 bg-zinc-200 w-full" />
            ))}
          </div>
        </div>
      ) : events.length === 0 ? (
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="text-center max-w-md bg-white border-2 border-black shadow-[6px_6px_0_0_rgba(0,0,0,1)] p-8">
            <Calendar className="w-16 h-16 mx-auto mb-4 text-zinc-300" />
            <h2 className="font-heading text-xl font-bold uppercase tracking-wide text-zinc-900 mb-2">No Timeline Data</h2>
            <p className="text-zinc-500 text-sm mb-6">Add start and end dates to your project phases to see them on the calendar.</p>
            <button
              onClick={() => navigate('/dashboard')}
              data-testid="gantt-go-dashboard"
              className="h-9 px-6 bg-primary text-black font-heading font-bold uppercase tracking-wider text-xs border-2 border-black shadow-[3px_3px_0_0_rgba(0,0,0,1)] hover:-translate-y-0.5 hover:shadow-[4px_4px_0_0_rgba(0,0,0,1)] transition-all"
            >
              Go to Projects
            </button>
          </div>
        </div>
      ) : (
        <>
          {view === 'week' && <WeekView anchor={anchor} events={events} onEventClick={handleEventClick} onEventCommit={handleEventCommit} />}
          {view === 'month' && <MonthView anchor={anchor} events={events} onEventClick={handleEventClick} onEventCommit={handleEventCommit} />}
          {view === 'quarter' && <QuarterView anchor={anchor} events={events} onEventClick={handleEventClick} />}
        </>
      )}

      {/* Projects without dates banner */}
      {!loading && noDateProjects.length > 0 && (
        <div className="flex-shrink-0 px-4 sm:px-6 py-2 bg-amber-50 border-t-2 border-amber-400 flex items-center gap-2 overflow-x-auto" data-testid="no-dates-banner">
          <Clock className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
          <span className="text-[10px] font-heading font-bold uppercase tracking-wider text-amber-700 flex-shrink-0">
            No Dates ({noDateProjects.length}):
          </span>
          <div className="flex items-center gap-3 min-w-max">
            {noDateProjects.map(p => (
              <button
                key={p.project_id}
                onClick={() => navigate(`/projects/${p.project_id}`)}
                className="text-xs text-amber-700 hover:text-amber-900 hover:underline truncate max-w-[180px]"
              >
                {p.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Phase detail panel */}
      {selectedPhase && selectedProject && (
        <PhasePanel
          phase={selectedPhase}
          project={selectedProject}
          onClose={() => { setSelectedPhase(null); setSelectedProject(null); }}
          onSave={handlePhaseDateSave}
        />
      )}
    </div>
  );
};

export default GanttPage;
